import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { OutsideBook } from "@/codes/gen/types";
import { OutsideBookService } from "@/codes/services/OutsideBookService";
import { ReactiveData } from "@/codes/types/MDOTypes";
import { DEFAULT_ITEMS_PER_PAGE, ROUTE_NAMES, VUEX_ACTIONS } from "@/codes/util/constants";
import { calculatePageCount, hasWritePermission } from "@/codes/util/functions";
import { Confirm, Toast } from "@/codes/util/swalUtil";
import router from "@/router";
import store from "@/store";
import { onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const loadingOutsideBooks = ref(false);
let page = 1;
const pageCount = ref(1);
const itemsPerPage = ref(DEFAULT_ITEMS_PER_PAGE);
const outsideBooks = reactive<ReactiveData<OutsideBook[]>>({ data: [] as OutsideBook[] });

let pageLoaded = 0;

const i18n = useI18n();

const headers = reactive({ data: [
  { key: 'name', label: i18n.t('register.name'), width: 200 },
  { key: 'qtScanned', label: i18n.t('book.scanned'), width: 60 },
  { key: 'qtExamDone', label: i18n.t('book.examDone'), width: 60 },
  { key: 'qtAbandoned', label: i18n.t('book.examAbandoned'), width: 60 },
  { key: 'campaign', label: i18n.t('campaign.title'), width: 130 },
  { key: 'agent', label: i18n.t('agent.title'), width: 130 },
]});

const checkHasWritePermission = function() {
  if (hasWritePermission(ROUTE_NAMES.outsideBooks)) {
    headers.data.push({ key: 'actions', label: i18n.t('form.actions'), width: 100 });
  }
}

const loadOutsideBooks = async function() {
  outsideBooks.data = [];
  loadingOutsideBooks.value = true;
  const service = new OutsideBookService();
  const usersEdge = await service.getAll({ page: page, perPage: itemsPerPage.value });
  if (usersEdge) {
    pageCount.value = calculatePageCount(itemsPerPage.value, usersEdge.pagination?.total ?? 1);
    outsideBooks.data = usersEdge.edges;
    pageLoaded = page;
  }
  loadingOutsideBooks.value = false;
}

const onPageChanged = function(newPage: number) {
  page = newPage;
  loadOutsideBooks();
}

onMounted(() => {
  loadOutsideBooks();

  store.subscribeAction({
    after: (action) => {
      if (action.type === VUEX_ACTIONS.setUserInfo) {
        checkHasWritePermission();
      }
    }
  });

  checkHasWritePermission();
});

const doNew = function() {
  router.push({ name: ROUTE_NAMES.newOutsideBook });
};

const edit = function(id: string) {
  router.push({ name: ROUTE_NAMES.editOutsideBook, params: { id: id }});
}

const share = function(book: OutsideBook) {
  navigator.clipboard.writeText(book.linkShare);
  Toast.fire({
    title: "Link de compartilhamento copiado!",
    icon: 'success'
  });
}

const download = function(book: OutsideBook) {
  const w = window.open(book.qrCode.image, '_blank');
  w?.focus();
}

const doRemove = async function(id: string) {
  const service = new OutsideBookService();
  const result = await service.remove(id);
  if (result && result.data.id) {
    outsideBooks.data = outsideBooks.data.filter((u) => u.id !== result.data.id);
      if (outsideBooks.data.length === 0) {
        if (page > 1) {
          onPageChanged(1);
        } else {
          window.location.reload();
        }
      }
  }
}

function remove(outsideBook: OutsideBook) {
  Confirm.fire({
    title: i18n.t('form.confirmDeleteTitle'),
    text: i18n.t('form.confirmDeleteMessage', { register: i18n.t('outsideBook.describe', { outsideBook: outsideBook.name }) })
  })
  .then((result) => {
    if (result.isConfirmed) {
      doRemove(outsideBook.id);
    }
  })
}


return (_ctx: any,_cache: any) => {
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_data_table = _resolveComponent("va-data-table")!
  const _component_base_list = _resolveComponent("base-list")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_base_list, {
      page: _unref(page),
      "page-count": pageCount.value,
      "current-route": _unref(ROUTE_NAMES).outsideBooks,
      "page-loaded": _unref(pageLoaded),
      "show-delete": false,
      onPageChanged: onPageChanged,
      onDoNew: doNew
    }, {
      default: _withCtx(() => [
        _createVNode(_component_va_data_table, {
          items: _unref(outsideBooks).data,
          columns: _unref(headers).data,
          "sticky-header": "",
          loading: loadingOutsideBooks.value,
          "no-data-html": "Sem resultados",
          style: _normalizeStyle({
          '--va-data-table-scroll-table-color': 'var(--va-divider)',
        })
        }, {
          "cell(campaign)": _withCtx(({ rowData }) => [
            _createTextVNode(_toDisplayString(rowData.campaign.name), 1)
          ]),
          "cell(agent)": _withCtx(({ rowData }) => [
            _createTextVNode(_toDisplayString(rowData.agent.name), 1)
          ]),
          "cell(actions)": _withCtx(({ rowData }) => [
            _createVNode(_component_va_button, {
              icon: "fas fa-download",
              class: "mr-1",
              color: "info",
              size: "small",
              onClick: ($event: any) => (download(rowData))
            }, null, 8, ["onClick"]),
            _createVNode(_component_va_button, {
              icon: "fas fa-share-nodes",
              class: "mr-1",
              color: "info",
              size: "small",
              onClick: ($event: any) => (share(rowData))
            }, null, 8, ["onClick"]),
            _createVNode(_component_va_button, {
              icon: "fas fa-edit",
              class: "mr-1",
              color: "info",
              size: "small",
              onClick: ($event: any) => (edit(rowData.id))
            }, null, 8, ["onClick"]),
            _createVNode(_component_va_button, {
              icon: "fas fa-trash",
              class: "danger",
              size: "small",
              onClick: ($event: any) => (remove(rowData))
            }, null, 8, ["onClick"])
          ]),
          _: 1
        }, 8, ["items", "columns", "loading", "style"])
      ]),
      _: 1
    }, 8, ["page", "page-count", "current-route", "page-loaded"])
  ]))
}
}

})